
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import OutlineButton from '@/components/ui/OutlineButton.vue';
import Modal from '@/components/ui/Modal.vue';
import ProposalSuppliersForm from '@/components/proposal/suppliers/ProposalSuppliersForm.vue';
import { ProposalAction } from '@/store/proposal/proposal.action';
import { ProposalMutation } from '@/store/proposal/proposal.mutation';
import { AppMutation } from '@/store/app/app.mutation';
import { NotificationType } from '@/store/app/notification/notification-type';
import { SuppliersSection } from '@/model/suppliers-choices';

export default defineComponent({
  name: 'ProposalProductsAdd',
  components: { ProposalSuppliersForm, Modal, OutlineButton },
  data() {
    return {
      modalVisibility: false,
      showNotice: false,
      applied: false,
      savedSections: [] as SuppliersSection[],
      skipped: [] as string[],
      added: { outbound: 0, returns: 0, surcharges: 0, brackets: 0 },
    };
  },
  computed: {
    ...mapGetters(['suppliersChoices']),
    clientId(): string {
      return typeof this.$route.params.clientId === 'string'
          ? this.$route.params.clientId
          : this.$route.params.clientId[0];
    },
    hasSelectedSuppliers(): boolean {
      return this.suppliersChoices?.suppliers?.some(
          (section: SuppliersSection) => (section.params?.supplierIds?.length ?? 0) > 0
      ) ?? false;
    },
    nothingAdded(): boolean {
      return this.added.outbound === 0 && this.added.returns === 0
          && this.added.surcharges === 0 && this.added.brackets === 0;
    },
  },
  watch: {
    modalVisibility(visible: boolean): void {
      // Closed without applying (cancel / X) → put the saved selection back.
      if (!visible && !this.applied) {
        this.$store.commit(ProposalMutation.SET_PROPOSAL_SUPPLIERS_SECTIONS, this.savedSections);
      }
      if (!visible) {
        this.applied = false;
      }
    },
  },
  methods: {
    openModal(): void {
      // Snapshot the current selection and swap in a single blank section so the form opens empty.
      this.savedSections = JSON.parse(JSON.stringify(this.suppliersChoices.suppliers));
      this.applied = false;
      this.$store.commit(ProposalMutation.SET_PROPOSAL_SUPPLIERS_SECTIONS, [{ params: {}, availableOptions: {} }]);
      this.$store.dispatch(ProposalAction.LOAD_PROPOSAL_AVAILABLE_SUPPLIERS, this.clientId);
      this.modalVisibility = true;
    },
    async handleAdd(): Promise<void> {
      this.applied = true;
      const pendingSections: SuppliersSection[] = JSON.parse(JSON.stringify(this.suppliersChoices.suppliers));
      // Restore the original selection and append the new section(s) so they persist on save.
      this.$store.commit(
          ProposalMutation.SET_PROPOSAL_SUPPLIERS_SECTIONS,
          [...this.savedSections, ...pendingSections]
      );

      try {
        const summary = await this.$store.dispatch(
            ProposalAction.ADD_SALE_PRICES_ROWS,
            { customerId: this.clientId, sections: pendingSections }
        );
        this.added = {
          outbound: summary.outbound,
          returns: summary.returns,
          surcharges: summary.surcharges,
          brackets: summary.brackets,
        };
        this.skipped = summary.skipped;
        this.showNotice = true;
      } catch (error) {
        this.$store.commit(AppMutation.ADD_NOTIFICATION, {
          type: NotificationType.ERROR,
          title: 'general.notifications.error',
          content: 'proposal.products.add.error',
        });
      }
    },
  },
});
